const ChangeCalculator = require('@/libraries/DigiTickets/Payment/ChangeCalculator');
const EventNames = require('@/lib/Events/EventNames');
const Payment = require('@/lib/Payments/Payment');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const { getMaxRefundableAmount, getRefundablePayments, isCurrentlyRefundable } = require('@/libraries/DigiTickets/Payment/refundablePayments');

/**
 * This is the controller for the inner part of the payment modal when partial Sage Pay payments are being partially or
 * fully refunded.
 *
 * @param $scope
 * @param $timeout
 * @param {CartService} cartService
 * @param {NumPadFactory} numPadFactory
 * @param {PaymentMethodService} paymentMethodService
 * @param {PrivilegesManager} privilegesManager
 */
const SagePayCtrl = function (
    $scope,
    $timeout,
    cartService,
    numPadFactory,
    paymentMethodService,
    privilegesManager,
) {
    $scope.paymentMethodScopes[PaymentMethodRef.CARD_SAGE_PAY] = $scope;
    $scope.paymentMethodScopes[PaymentMethodRef.CARD_SAGE_PAY_BACK_OFFICE] = $scope;
    $scope.paymentMethodScopes[PaymentMethodRef.CARD_SAGEPAY_SERVER] = $scope;

    const sagePayPaymentMethods = [
        PaymentMethodRef.CARD_SAGE_PAY,
        PaymentMethodRef.CARD_SAGE_PAY_BACK_OFFICE,
        PaymentMethodRef.CARD_SAGEPAY_SERVER,
    ];

    // We don't hard code this value because it could be any of 3 payment methods defined in sagePayPaymentMethods[].
    $scope.thisPaymentMethodRef = null;

    $scope.cart = cartService;

    $scope.maxRefundableAmount = 0;

    $scope.paymentIndex = null;

    $scope.refundAmountError = false;

    /**
     * The payment that the operator has selected to be refunded. If this is populated the numPad is visible otherwise
     * the list of payments is shown.
     *
     * @type {Payment|null}
     */
    $scope.paymentToBeRefunded = null;

    /**
     * The amount entered.
     *
     * @type {number}
     */
    $scope.refundAmountEntered = 0;

    $scope.calculator = new ChangeCalculator();

    /**
     * List all the existing payments that are potentially refundable for this order.
     *
     * @returns {Payment[]}
     */
    $scope.getRefundablePayments = function () {
        return getRefundablePayments(
            cartService.currentCart.originalOrder.payments,
            sagePayPaymentMethods
        );
    };

    $scope.numPad = numPadFactory.create(
        {
            showCashButtons: true,
            showSignButton: false,
        }
    );
    $scope.numPad.onChange((value) => {
        // When the number pad value changes update validate the amount and update the calculator.
        if ($scope.cart.isRefund() && value < $scope.maxRefundableAmount) {
            $scope.refundAmountError = true;
        } else {
            $scope.refundAmountError = false;
            $scope.calculator.setAmountTendered(value);
            $scope.setNumPadTotal(value);
        }

        $scope.refundAmountEntered = value;
    });
    $scope.numPadListening = false;

    $scope.clear = function () {
        $scope.calculator.clear();
        $scope.numPad.clear();
        // Clearing the selected Payment hides the num pad.
        $scope.paymentToBeRefunded = null;
    };

    $scope.setAmountDue = function (amount) {
        $scope.calculator.setAmountDue(amount);
        $scope.numPad.setExactAmount(amount);
        $scope.numPad.options.isNegative = amount < 0;
    };

    $scope.setAmountDue($scope.cart.getRemainingBalance());

    /**
     * Select a payment to refund from the list of payments that are currently refundable.
     *
     * @param {Payment} payment
     * @param {Number} index Identifier to distinguish between payments easily.
     */
    $scope.selectPayment = function (payment, index) {
        privilegesManager.requirePrivilege(PrivilegeNames.PARTIAL_REFUND_OF_EXISTING_PAYMENT).then((result) => {
            if (result.granted) {
                $timeout(() => {
                    $scope.paymentIndex = index + 1;
                    $scope.paymentToBeRefunded = payment;
                    $scope.maxRefundableAmount = getMaxRefundableAmount(
                        $scope.paymentToBeRefunded.outstandingRefundableAmount,
                        $scope.cart.getRemainingBalance()
                    );
                });
            }
        });
    };

    /**
     *  Close the num pad and return to the select payment screen.
     */
    $scope.back = function () {
        // Calling clear() resets the numpad amount and any errors due to invalid amounts.
        $scope.clear();
    };

    /**
     * Is this payment currently refundable? The outstanding refundable balance must be > 0 and there cannot already be
     * a pending refund against this payment.
     *
     * @param {Payment} payment
     *
     * @returns {boolean}
     */
    $scope.isCurrentlyRefundable = function (payment) {
        return isCurrentlyRefundable(payment, $scope.cart.payments);
    };

    /**
     * Populate payment.paymentCompositeID - in this case just a simple identifier e.g. SAGE_PAY_SERVER.1 to associate a
     * selected/pending payment with a specific available payment.
     *
     * @returns {number}
     */
    $scope.setPaymentCompositeID = function () {
        $scope.paymentToBeRefunded.paymentCompositeID = $scope.thisPaymentMethodRef + '.' + $scope.paymentIndex;
        return $scope.paymentToBeRefunded.paymentCompositeID;
    };

    $scope.$on('payment-modal.payment-removed', function (event, removedPayment) {
        $scope.setAmountDue($scope.cart.getRemainingBalance());
        if (removedPayment.paymentCompositeID) {
            const parts = removedPayment.paymentCompositeID.split('.');
            if (parts[0] && sagePayPaymentMethods.includes(parts[0])) {
                const index = cartService.currentCart.originalOrder.payments.findIndex(
                    (payment) => payment.paymentCompositeID === removedPayment.paymentCompositeID
                );
                if (cartService.currentCart.originalOrder.payments[index]) {
                    cartService.currentCart.originalOrder.payments[index].pendingPartialRefundAmount = null;
                }
            }
        }
    });

    $scope.$on('payment-modal.cancel-pressed', function (event, paymentMethodRef) {
        if (paymentMethodRef === $scope.thisPaymentMethodRef) {
            // Was cancelled from the cash tab.
            $scope.clear();
            $scope.setAmountDue($scope.cart.getRemainingBalance());
        }
    });

    $scope.$on(
        'payment-modal.edit-payment-pressed',
        /**
         * @param event
         * @param {Payment} existingPayment
         */
        function (event, existingPayment) {
            if (sagePayPaymentMethods.includes(existingPayment.paymentMethod.ref)) {
                $scope.selectPaymentMethod(existingPayment.paymentMethod);
                $scope.setAmountDue($scope.cart.getRemainingBalance());
                if (existingPayment.paymentCompositeID) {
                    const index = cartService.currentCart.originalOrder.payments.findIndex(
                        (payment) => payment.paymentCompositeID === existingPayment.paymentCompositeID
                    );
                    if (cartService.currentCart.originalOrder.payments[index]) {
                        cartService.currentCart.originalOrder.payments[index].pendingPartialRefundAmount = null;
                        $scope.selectPayment(cartService.currentCart.originalOrder.payments[index], index);
                        $scope.numPad.setValue(existingPayment.amount);
                    }
                }
            }
        }
    );

    $scope.$on(EventNames.PAYMENT_METHOD_CHANGED, function (event, newPaymentMethod) {
        const newPaymentMethodRef = newPaymentMethod?.ref;
        // Changing tab, so reset the calculator.
        $scope.clear();
        $scope.setAmountDue($scope.cart.getRemainingBalance());

        if (sagePayPaymentMethods.includes(newPaymentMethodRef)) {
            $scope.thisPaymentMethodRef = newPaymentMethodRef;
        }

        $scope.numPadListening = newPaymentMethodRef === $scope.thisPaymentMethodRef;
    });

    $scope.$on('payment-modal.ok-pressed', function (event, paymentMethodRef) {
        if (paymentMethodRef === $scope.thisPaymentMethodRef) {
            let amountTendered = $scope.calculator.getAmountTendered();
            let thisPaymentMethod = paymentMethodService.getByRef($scope.thisPaymentMethodRef);

            $scope.$parent.clearError();

            if ($scope.cart.isRefund() && amountTendered < 0) {
                // The PaymentService will separate this payment from any other payments and send it to the refund
                // endpoint which will in turn call the payment gateway to process the refund request.
                let payment = new Payment();
                payment.setPaymentMethod(thisPaymentMethod);
                payment.setTendered($scope.refundAmountEntered);
                payment.setThirdPartyID($scope.cart.generatePaymentThirdPartyID());
                // The link to the original payment that we are (partially) refunding.
                payment.setOriginalPaymentID($scope.paymentToBeRefunded.ID);
                payment.setValue($scope.refundAmountEntered);
                payment.paymentCompositeID = $scope.setPaymentCompositeID();
                $scope.paymentToBeRefunded.pendingPartialRefundAmount = $scope.refundAmountEntered * -1;
                $scope.addPayment(payment);
            }

            // Clear for next use.
            $scope.clear();
            $scope.setAmountDue($scope.cart.getRemainingBalance());
        }
    });
};

module.exports = SagePayCtrl;
